import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { IAssetLabel } from '../models/assetLabel';
import {
  IBatchShootArchiveRequest,
  IShootAssetArchiveResponse,
} from '../models/batches/batchArchive';
import {
  PreviewShootAssetArchiveRequest,
  PreviewShootAssetArchiveResponse,
} from '../models/batches/shoot-asset-archive-preview';
import {
  AssetMetaDataResult,
  AssetLabelResult,
  AssetDeleteResult,
  PinAssetResult,
} from '../models/assets/assets-result';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }),
};

@Injectable({
  providedIn: 'root',
})
export class AssetsService {
  constructor(private http: HttpClient) {}

  getAssetMetaData(assetId: string, assetType: string): Observable<AssetMetaDataResult> {
    const url = `${environment.apiBase}/${assetType}/${assetId}/meta-data`;
    return this.http.get<AssetMetaDataResult>(url, httpOptions);
  }

  generateAssetMetaData(assetId: string, assetType: string): Observable<void> {
    const url = `${environment.apiBase}/${assetType}/${assetId}/meta-data/generate`;
    return this.http.patch<void>(url, httpOptions);
  }

  pinAsset(id: string, entity: string, state: boolean): Observable<PinAssetResult> {
    const url = `${environment.apiBase}/${entity}/${id}/pin/${state}`;
    return this.http.patch<PinAssetResult>(url, httpOptions);
  }

  deleteAsset(assetId: string, assetType: string): Observable<AssetDeleteResult> {
    const url = `${environment.apiBase}/${assetType}/${assetId}`;
    return this.http.delete<AssetDeleteResult>(url, httpOptions);
  }

  parseAssetLabel(assetId: string, assetType: string): Observable<void> {
    const url = `${environment.apiBase}/${assetType}/${assetId}/asset-label`;
    return this.http.post<void>(url, httpOptions);
  }

  getAssetLabel(assetId: string, assetType: string): Observable<IAssetLabel[]> {
    const url = `${environment.apiBase}/${assetType}/${assetId}/asset-label`;
    return this.http.get<IAssetLabel[]>(url, httpOptions);
  }

  saveAssetLabel(assetId: string, assetType: string, label: unknown): Observable<AssetLabelResult> {
    const url = `${environment.apiBase}/${assetType}/${assetId}/save-label`;
    return this.http.post<AssetLabelResult>(url, { label }, httpOptions);
  }

  extractLabelBoundingBox(assetId: string, assetType: string): Observable<void> {
    const url = `${environment.apiBase}/${assetType}/${assetId}/extract-content`;
    return this.http.get<void>(url, httpOptions);
  }

  resetAssetContentExtracting(assetId: string, assetType: string): Observable<void> {
    const url = `${environment.apiBase}/${assetType}/${assetId}/reset-content-extraction`;
    return this.http.post<void>(url, {}, httpOptions);
  }

  getAvailableGptModels(): Observable<string[]> {
    const url = `${environment.apiBase}/available-gpt-models`;
    return this.http.get<string[]>(url, httpOptions);
  }

  getAvailableRtProfiles(): Observable<string[]> {
    const url = `${environment.apiBase}/available-rt-profiles`;
    return this.http.get<string[]>(url, httpOptions);
  }

  getAvailableAutoPipelines(): Observable<string[]> {
    const url = `${environment.apiBase}/available-auto-pipelines`;
    return this.http.get<string[]>(url, httpOptions);
  }

  generatePostAsset(
    id: string,
    model?: string,
    rtProfile?: string,
    pipeline?: string,
  ): Observable<void> {
    let url = `${environment.apiBase}/shoot-asset/${id}/generate-post-asset`;
    if (model) url += `?model=${model}`;
    if (rtProfile) url += `&rtProfile=${rtProfile}`;
    if (pipeline) url += `&pipeline=${pipeline}`;
    return this.http.post<void>(url, httpOptions);
  }

  createShootAssetArchive(
    batchShootAssetArchive: IBatchShootArchiveRequest,
  ): Observable<IShootAssetArchiveResponse> {
    const url: string = `${environment.apiBase}/shoot-asset/generate-download-archive`;
    return this.http.post<IShootAssetArchiveResponse>(url, batchShootAssetArchive, httpOptions);
  }

  previewShootAssetArchive(
    request: PreviewShootAssetArchiveRequest,
  ): Observable<PreviewShootAssetArchiveResponse> {
    const url: string = `${environment.apiBase}/shoot-asset/preview-archive`;
    return this.http.post<PreviewShootAssetArchiveResponse>(url, request, httpOptions);
  }
}
